import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, Download, Filter, ChevronLeft, ChevronRight, Globe, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ISO to Country Flag Emoji mapping
const getFlagEmoji = (iso) => {
  if (!iso || iso.length < 2) return '🌍';
  
  // Map some special cases
  const specialCases = {
    'AB': '🇬🇪', // Abkhazia - use Georgia flag
    'AC': '🇸🇭', // Ascension Island - use St Helena
    'AN': '🇳🇱', // Netherlands Antilles
    'XK': '🇽🇰', // Kosovo
  };
  
  if (specialCases[iso]) return specialCases[iso];
  
  // Convert ISO to flag emoji
  try {
    const codePoints = iso
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch {
    return '🌍';
  }
};

const currencies = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "Pound" },
  { code: "INR", symbol: "₹", name: "Rupee" }
];

const RatesPage = () => {
  const [rates, setRates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedPlan, setSelectedPlan] = useState("gold");
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  const fetchCountries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/countries`);
      setCountries(res.data.countries || []);
    } catch (e) {
      console.error("Failed to fetch countries", e);
    }
  }, []);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: pageSize,
        plan: selectedPlan,
        currency: selectedCurrency
      };
      if (search) params.search = search;
      if (selectedCountry && selectedCountry !== "all") params.country = selectedCountry;
      
      const res = await axios.get(`${API}/rates`, { params });
      setRates(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error("Failed to fetch rates", e);
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedCountry, selectedPlan, selectedCurrency]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(1);
      fetchRates();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, selectedCountry, selectedPlan, selectedCurrency]);

  useEffect(() => {
    fetchRates();
  }, [page, fetchRates]);

  const handleDownload = () => {
    window.open(`${API}/rates/download?plan=${selectedPlan}`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'increase': return 'text-red-600 bg-red-50';
      case 'decrease': return 'text-green-600 bg-green-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getCurrencySymbol = () => {
    const curr = currencies.find(c => c.code === selectedCurrency);
    return curr ? curr.symbol : '$';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rates-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 noise-bg" data-testid="rates-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Call Rates</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Competitive rates for 235+ countries. Search by destination or browse by country.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Plan Tabs & Currency Selector */}
      <section className="bg-white border-b py-4" data-testid="plan-tabs">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Plan Tabs */}
            <Tabs value={selectedPlan} onValueChange={setSelectedPlan} className="w-full md:w-auto">
              <TabsList className="grid grid-cols-3 w-full md:w-[400px]">
                <TabsTrigger value="silver" className="data-[state=active]:bg-slate-600 data-[state=active]:text-white">
                  Silver
                </TabsTrigger>
                <TabsTrigger value="gold" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  Gold
                </TabsTrigger>
                <TabsTrigger value="platinum" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                  Platinum
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            {/* Currency Selector */}
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-500 mr-2">Currency:</span>
              <div className="flex gap-1">
                {currencies.map((curr) => (
                  <Button
                    key={curr.code}
                    variant={selectedCurrency === curr.code ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCurrency(curr.code)}
                    className={`px-3 ${selectedCurrency === curr.code ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    data-testid={`currency-${curr.code.toLowerCase()}`}
                  >
                    {curr.symbol} {curr.code}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="sticky top-20 bg-white shadow-md z-40 py-4" data-testid="rates-filters">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by destination, country or prefix..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 py-6 w-full rounded-xl border-slate-200 focus:border-red-500"
                data-testid="rates-search-input"
              />
            </div>
            
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-full md:w-[220px] py-6 rounded-xl" data-testid="rates-country-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">🌍 All Countries</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c.iso} value={c.iso}>
                    {getFlagEmoji(c.iso)} {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              onClick={handleDownload}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-6 py-6 whitespace-nowrap"
              data-testid="rates-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          </div>
          
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span data-testid="rates-count">
              Showing {rates.length} of {total.toLocaleString()} routes 
              <span className="ml-2 px-2 py-1 bg-slate-100 rounded text-xs font-medium uppercase">{selectedPlan}</span>
            </span>
            <span>Page {page} of {totalPages}</span>
          </div>
        </div>
      </section>

      {/* Rates Table */}
      <section className="py-8" data-testid="rates-table-section">
        <div className="container-custom">
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="animate-pulse">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="flex gap-4 p-4 border-b border-slate-100">
                    <div className="h-5 bg-slate-200 rounded w-10" />
                    <div className="h-5 bg-slate-200 rounded w-20" />
                    <div className="h-5 bg-slate-200 rounded w-40 flex-1" />
                    <div className="h-5 bg-slate-200 rounded w-24" />
                    <div className="h-5 bg-slate-200 rounded w-16" />
                  </div>
                ))}
              </div>
            </div>
          ) : rates.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No routes found</h3>
              <p className="text-slate-500">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full rate-table" data-testid="rates-table">
                  <thead>
                    <tr>
                      <th className="text-left">Country</th>
                      <th className="text-left">Prefix</th>
                      <th className="text-left">Destination</th>
                      <th className="text-left">Rate ({getCurrencySymbol()}/min)</th>
                      <th className="text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((rate, i) => (
                      <motion.tr
                        key={`${rate.prefix}-${i}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-red-50 transition-colors"
                      >
                        <td className="font-medium">
                          <span className="text-2xl mr-2">{getFlagEmoji(rate.iso)}</span>
                          <span className="text-slate-600 text-sm">{rate.country_name || rate.iso}</span>
                        </td>
                        <td className="font-mono text-slate-900">
                          {rate.prefix}
                          {rate.prefix_count > 1 && (
                            <span className="ml-1 text-xs text-slate-400">({rate.prefix_count})</span>
                          )}
                        </td>
                        <td className="font-medium text-slate-700">{rate.route_name}</td>
                        <td className="font-semibold text-red-600">
                          {getCurrencySymbol()}{rate.rate?.toFixed(4)}
                        </td>
                        <td>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(rate.status)}`}>
                            {rate.status || 'Active'}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8" data-testid="rates-pagination">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              
              <div className="flex items-center gap-2">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-full p-0 ${page === pageNum ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded-full"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Info Banner */}
      <section className="bg-slate-900 text-white py-12 noise-bg">
        <div className="container-custom text-center">
          <p className="text-slate-300 mb-4">
            Need custom rates or volume discounts? Contact our sales team.
          </p>
          <a href="https://t.me/cyberx007" target="_blank" rel="noopener noreferrer">
            <Button className="bg-red-600 hover:bg-red-700 rounded-full px-6">
              Chat on Telegram
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default RatesPage;
